import React from 'react';

import { Anchor, Group, Switch, Text } from '@mantine/core';
import { useDisclosure, useViewportSize } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import HelpButton from 'components/HelpButton';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useKiloGLMFlows from 'hooks/glmFlows/useKiloGLMFlows';
import useUpdateKiloGLMFlow from 'hooks/glmFlows/useUpdateKiloGLMFlow';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { KiloGLMFlowList } from 'types';
import notificationHandler from 'utils/notifications';

import FlowsEmptyState from './FlowsEmptyState';
import ModalDeleteKiloGLMFlow from './ModalDeleteKiloGLMFlow';
import ModalKiloGLMRecords from './ModalKiloGLMRecords';
import {
  ACTION_LABELS,
  KiloGLMFlowActions,
  KiloGLMFlowTriggers,
  TRIGGER_DESCRIPTIONS,
  TRIGGER_LABELS,
} from '../../KiloGLMFlowBuilder/types';

const KiloGLMFlowTableContainer = (): JSX.Element => {
  const { data, isLoading, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useKiloGLMFlows();
  const [selectedFlow, setSelectedFlow] = React.useState<KiloGLMFlowList | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [recordsFlow, setRecordsFlow] = React.useState<KiloGLMFlowList | null>(null);
  const [recordsModalOpened, { open: openRecordsModal, close: closeRecordsModal }] = useDisclosure(false);
  const navigate = useNavigate();
  const { width: viewportWidth } = useViewportSize();
  const detailsMaxWidth = Math.max(120, Math.min(800, Math.round(viewportWidth * 0.3)));
  const { mutate: updateFlow } = useUpdateKiloGLMFlow();
  const { trackGLMFlowToggled } = useStaffTracking();
  const { successNotification, errorNotification } = notificationHandler();

  const handleToggleEnabled = (flow: KiloGLMFlowList): void => {
    updateFlow(
      {
        kiloGlmFlowId: flow.kiloGlmFlowId,
        trigger: flow.trigger,
        action: flow.action,
        enabled: !flow.enabled,
        tcAthleteStatus: flow.tcAthleteStatus,
        acPipelineId: flow.acPipelineId,
        acStageId: flow.acStageId,
        acTags: flow.acTags,
        tcCheckinsNumber: flow.tcCheckinsNumber,
        packageIds: flow.tcPackages?.map(p => p.packageId) || [],
        classTypeIds: flow.tcClassTypes?.map(c => c.kiloClassTypeId) || [],
        appointmentTypeIds: flow.tcAppointmentTypes?.map(a => a.appointmentTypeId) || [],
      },
      {
        onSuccess: () => {
          trackGLMFlowToggled({
            flow_id: flow.kiloGlmFlowId,
            direction: 'kilo_to_glm',
            trigger: flow.trigger,
            action: flow.action,
            enabled: !flow.enabled,
          });
          successNotification('Flow updated', `Flow ${!flow.enabled ? 'enabled' : 'disabled'}.`);
        },
        onError: error => {
          errorNotification('Flow update failed', error.response?.data.message);
        },
      },
    );
  };

  const handleDelete = (flow: KiloGLMFlowList): void => {
    setSelectedFlow(flow);
    openDeleteModal();
  };

  const handleEdit = (flow: KiloGLMFlowList): void => {
    navigate(`builder/${flow.kiloGlmFlowId}`);
  };

  const getEmptyState = (): JSX.Element => (
    <FlowsEmptyState
      description='Set one up to start syncing your Kilo data to GLM automatically.'
      onCreate={() => navigate('/integrations/glm/builder')}
    />
  );

  const columns = [
    {
      Header: 'When this happens in Kilo',
      accessor: 'trigger' as const,
      Cell: ({ value }: { value: KiloGLMFlowList['trigger'] }) => {
        const trigger = value as KiloGLMFlowTriggers;
        return (
          <Group spacing={4} noWrap>
            <Text>{TRIGGER_LABELS[trigger] ?? value}</Text>
            {TRIGGER_DESCRIPTIONS[trigger] && (
              <HelpButton width={260} position='top-start' withinPortal>
                <Text size='sm' style={{ whiteSpace: 'normal' }}>
                  {TRIGGER_DESCRIPTIONS[trigger]}
                </Text>
              </HelpButton>
            )}
          </Group>
        );
      },
    },
    {
      Header: 'Details',
      id: 'triggerConfig',
      Cell: ({ row }: CellProps<KiloGLMFlowList>) => {
        const { trigger, tcPackages, tcAthleteStatus, tcCheckinsNumber, tcAppointmentTypes, tcClassTypes } =
          row.original;
        let detail = '-';
        if (trigger.toLowerCase().includes('package')) {
          detail = tcPackages.map(p => p.name).join(', ');
        } else if (trigger === 'Athlete status changed' && tcAthleteStatus) {
          detail = tcAthleteStatus;
        } else if (trigger === 'Athlete checkins reached' && tcCheckinsNumber != null) {
          detail = `${tcCheckinsNumber} check-ins`;
        } else if (trigger === 'Appointment check-in' && tcAppointmentTypes) {
          detail = tcAppointmentTypes.map(a => a.name).join(', ');
        } else if (trigger === 'Class check-in' && tcClassTypes) {
          detail = tcClassTypes.map(c => c.name).join(', ');
        }
        return (
          <Text truncate maw={detailsMaxWidth} title={detail}>
            {detail}
          </Text>
        );
      },
    },
    {
      Header: 'Do this in GLM',
      accessor: 'action' as const,
      Cell: ({ value }: { value: KiloGLMFlowList['action'] }) => (
        <Text>{ACTION_LABELS[value as KiloGLMFlowActions] ?? value}</Text>
      ),
    },
    {
      Header: 'Details',
      id: 'actionConfig',
      Cell: ({ row }: CellProps<KiloGLMFlowList>) => {
        const { acTags, acPipelineId, acStageId, acPipelineName, acStageName } = row.original;
        let detail = '-';
        if (acTags?.length) {
          detail = acTags.join(', ');
        } else if (acPipelineId || acStageId) {
          detail =
            [acPipelineName ?? acPipelineId, acStageName ?? acStageId].filter(Boolean).join(' → ') || '-';
        }
        return (
          <Text truncate maw={detailsMaxWidth} title={detail}>
            {detail}
          </Text>
        );
      },
    },
    {
      Header: 'Executions',
      accessor: 'timesExecuted' as const,
      Cell: ({ row }: CellProps<KiloGLMFlowList>) => (
        <Anchor
          onClick={() => {
            setRecordsFlow(row.original);
            openRecordsModal();
          }}
        >
          {row.original.timesExecuted}
        </Anchor>
      ),
    },
    {
      Header: 'Active',
      accessor: 'enabled' as const,
      Cell: ({ row }: CellProps<KiloGLMFlowList>) => (
        <Switch checked={row.original.enabled} onChange={() => handleToggleEnabled(row.original)} />
      ),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<KiloGLMFlowList>) => (
        <Group>
          <Menu
            items={[
              {
                title: 'Edit',
                onClick: () => handleEdit(row.original),
                icon: <IconEdit size={16} />,
              },
              {
                title: 'Delete',
                onClick: () => handleDelete(row.original),
                color: 'red',
                icon: <IconTrashX size={16} />,
              },
            ]}
          />
        </Group>
      ),
    },
  ];
  return (
    <>
      <Table
        tableData={data?.pages.map(page => page.data.results).flat() || []}
        columns={data?.pages.map(page => page.data.results).flat().length ? columns : []}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage ?? false}
        fetchNextPage={fetchNextPage}
        EmptyState={getEmptyState()}
      />
      {selectedFlow && (
        <ModalDeleteKiloGLMFlow
          opened={deleteModalOpened}
          handleClose={closeDeleteModal}
          flow={selectedFlow}
        />
      )}
      <ModalKiloGLMRecords
        opened={recordsModalOpened}
        onClose={closeRecordsModal}
        flowId={recordsFlow?.kiloGlmFlowId ?? null}
        flowLabel={recordsFlow?.trigger}
      />
    </>
  );
};
export default KiloGLMFlowTableContainer;
